/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';

const PROMOTE_ERROR_LANG_MESSAGE = 'graphList.PROMOTE_ERROR_MESSAGE';

/**
 * PromoteGraphService - Promote graph to target environment
 * API: POST /graphs/:graphId/promote
 *
 * Services payload: { graphName, graphData, targetEnv }
 */
export default class PromoteGraphService extends BaseService {
  constructor() {
    super(constants.PROMOTE_GRAPH, 'promoteGraphStatus');
  }

  getErrorMessage(data, fallback) {
    return data?.error || data?.message || fallback;
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, graphName, graphData, targetEnv } = payload;

    const requestData = {
      graphName,
      graphData,
      targetEnv
    };

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').post(`/graphs/${graphId}/promote`, requestData, {
        headers: enhancedHeaders
      });
    }

    return this.getClient('aiadmin').post(`/graphs/${graphId}/promote`, requestData, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    const graphModel = Framework.getStoreRegistry().getStore('graphModel');

    if (data && data.success) {
      graphModel.clearError();
      Framework.getEventManager().publish(constants.PROMOTE_GRAPH_SUCCESS, { data });
      return;
    }

    const msg = this.getErrorMessage(data, PROMOTE_ERROR_LANG_MESSAGE);
    Framework.getStoreRegistry().getStore('notificationState').set({
      open: true,
      type: 'error',
      langMessage: PROMOTE_ERROR_LANG_MESSAGE,
      message: msg
    });
    graphModel.setError(msg);
    Framework.getEventManager().publish(constants.PROMOTE_GRAPH_ERROR);
  }

  handleError(error) {
    const { response } = error;
    const graphModel = Framework.getStoreRegistry().getStore('graphModel');

    if (response?.status === 401) {
      utils.redirectToLogin();
    } else if (response) {
      const msg = this.getErrorMessage(response.data, PROMOTE_ERROR_LANG_MESSAGE);
      Framework.getStoreRegistry().getStore('notificationState').set({
        open: true,
        type: 'error',
        langMessage: PROMOTE_ERROR_LANG_MESSAGE,
        message: msg
      });
      graphModel.setError(msg);
    } else {
      Framework.getStoreRegistry().getStore('notificationState').set({
        open: true,
        type: 'error',
        langMessage: PROMOTE_ERROR_LANG_MESSAGE
      });
      graphModel.setError(PROMOTE_ERROR_LANG_MESSAGE);
    }

    Framework.getEventManager().publish(constants.PROMOTE_GRAPH_ERROR);
  }
}
